import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { House, ChatCircle, Heart, FileText, CalendarBlank, GraduationCap } from '@phosphor-icons/react';
import { CompactFooter } from './CompactFooter';

export const MainLayout = ({ children, noPadding = false, desktopWide = false, hideFooter = false }) => {
  const location = useLocation();
  const navigate = useNavigate();

  // Stage-14 — task-first IA. User-facing labels reframed around the 5 jobs:
  //   Ask · Upload · Prepare · Review what changed · Manage record.
  //   Wave 1 fix sprint (2026-02): added Learn as 6th item — University
  //   surface was previously orphaned (no nav entry from any page).
  //   Routes are unchanged so no deep-links break.
  const navItems = [
    { path: '/home',         icon: House,         label: 'Home' },
    { path: '/chat',         icon: ChatCircle,    label: 'Ask' },
    { path: '/documents',    icon: FileText,      label: 'Reports' },
    { path: '/appointments', icon: CalendarBlank, label: 'Appts' },
    { path: '/health',       icon: Heart,         label: 'Record' },
    { path: '/learn',        icon: GraduationCap, label: 'Learn' },
  ];

  return (
    <div className="min-h-screen bg-[#FAFAFA] flex flex-col overflow-x-hidden">
      {/* Content area. Mobile: edge-to-edge with px-4. Desktop (>=lg): hand
          the full width to the page so it can build its own multi-column
          shell. `desktopWide` means "page handles its own padding"; default
          mobile padding still applies <lg.
          Wave 1 fix sprint: bumped bottom padding from 72px to 96px so the
          last row of content never visually clips into the floating nav. */}
      <div className="flex-1 safe-top" style={{ paddingBottom: 'calc(96px + env(safe-area-inset-bottom, 0px))' }}>
        <div className={
          noPadding
            ? ''
            : (desktopWide ? 'px-4 sm:px-6 py-4 lg:px-0 lg:py-0' : 'px-4 sm:px-6 py-4 lg:max-w-[760px] lg:mx-auto')
        }>
          {children}
          {/* Wave G-A · subtle compact footer (Privacy · Terms · Security · Contact).
              Renders at the end of every authed page's scroll area; bottom nav
              floats above. Marketing/public pages keep their richer footer.
              `hideFooter` lets task surfaces (active chat, full-screen flows)
              opt out so it never clutters mobile thumb workflows. */}
          {!hideFooter && <CompactFooter />}
        </div>
      </div>

      {/* Bottom nav: sits above safe area. Stage 4 — frosted glass, refined contrast */}
      <nav
        className="fixed bottom-0 left-0 right-0 z-50 border-t border-[#ECE8E1] bg-white/85"
        style={{
          paddingBottom: 'env(safe-area-inset-bottom, 0px)',
          backdropFilter: 'saturate(180%) blur(18px)',
          WebkitBackdropFilter: 'saturate(180%) blur(18px)',
        }}
      >
        <div className="max-w-lg mx-auto flex items-center justify-around px-1 py-1.5">
          {navItems.map(({ path, icon: Icon, label }) => {
            const isActive = location.pathname === path ||
              (path === '/appointments' && location.pathname.startsWith('/appointments')) ||
              (path === '/learn' && location.pathname.startsWith('/learn'));
            return (
              <button
                key={path}
                data-testid={`nav-${label.toLowerCase()}`}
                onClick={() => navigate(path)}
                className={`relative flex flex-col items-center justify-center gap-0.5 min-w-[48px] min-h-[48px] px-1 rounded-xl transition-colors ${
                  isActive ? 'text-[#0E1C2E]' : 'text-slate-400 hover:text-slate-600 active:text-slate-700'
                }`}
              >
                {/* Stage 20B+ Wave B — active-state accent dot. Subtle teal
                    pip above the icon; only renders for the active route. */}
                {isActive && (
                  <span
                    aria-hidden
                    className="absolute top-[2px] w-1 h-1 rounded-full"
                    style={{ backgroundColor: '#2FB3A0' }}
                  />
                )}
                <Icon size={22} weight={isActive ? 'fill' : 'regular'} />
                <span className={`text-[10px] leading-none ${isActive ? 'font-semibold' : 'font-medium'}`}>{label}</span>
              </button>
            );
          })}
        </div>
      </nav>
    </div>
  );
};
